import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Tooltip from '@mui/material/Tooltip';
import { VALIDATION_STATUS } from 'utils/validationResultUtils';
import { resolveIconAndColor } from './validationAlertUtils';
import styles from './ValidationAlert.module.css';

const HTML_ENTITIES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const escapeHtml = (input) => String(input ?? '').replace(/[&<>"']/g, (ch) => HTML_ENTITIES[ch]);

const NO_TRANSLATE_PROPS = {
    translate: 'no',
    'data-no-translate': 'true',
    'data-gramm': 'false',
    'data-gramm_editor': 'false',
    'data-enable-grammarly': 'false',
};

function ValidationAlert({ status, message, color = null, hasAutofill = false, isSheetAnnouncement = false }) {
    const { Icon, backgroundColor } = useMemo(
        () => resolveIconAndColor({ status, color, hasAutofill, isSheetAnnouncement }),
        [status, color, hasAutofill, isSheetAnnouncement]
    );

    const tooltipTitle = useMemo(() => {
        if (!message) return '';
        return <span className="notranslate" {...NO_TRANSLATE_PROPS} dangerouslySetInnerHTML={{ __html: escapeHtml(message) }} />;
    }, [message]);

    const tooltipSlotProps = useMemo(
        () => ({
            tooltip: {
                className: 'notranslate',
                ...NO_TRANSLATE_PROPS,
                sx: {
                    backgroundColor,
                    color: '#ffffff',
                    fontSize: 10,
                    fontWeight: 500,
                    lineHeight: '14px',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    maxWidth: 320,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    '& .MuiTooltip-arrow': { color: backgroundColor },
                },
            },
            popper: { sx: { zIndex: 100000 } },
        }),
        [backgroundColor]
    );

    if (!status) return null;

    const iconElement = (
        <div className={styles['validation-alert-wrapper']}>
            <div className={styles['validation-icon-container']} style={{ backgroundColor, borderColor: backgroundColor }}>
                <Icon size={18} strokeWidth={2.5} color="#ffffff" />
            </div>
        </div>
    );

    if (!message) return iconElement;

    return (
        <Tooltip title={tooltipTitle} arrow placement="top" disableInteractive enterDelay={0} leaveDelay={0} slotProps={tooltipSlotProps}>
            {iconElement}
        </Tooltip>
    );
}

ValidationAlert.propTypes = {
    status: PropTypes.oneOf(Object.values(VALIDATION_STATUS)),
    message: PropTypes.string,
    color: PropTypes.string,
    hasAutofill: PropTypes.bool,
    isSheetAnnouncement: PropTypes.bool,
};

export default React.memo(ValidationAlert);
